import React, { createContext, useState, useEffect, useRef, useCallback } from 'react';
import { 
  fetchEditions,
  fetchVerseData 
} from '../utils/api';
import {
  loadReadingProgress,
  saveReadingProgress
} from '../utils/localStorage';
import { surahHasBismillah, nextPosition, previousPosition } from '../utils/helpers';
import { fetchVerseCached, prefetchVerse } from '../utils/verseCache';
import { parseDeepLink, formatDeepLink } from '../utils/deepLink';
import { ensurePack, verseFromPack } from '../utils/offlineQuran';
import { STORAGE_KEYS } from '../utils/constants';
import { parseSajdaData, isSajdaVerse, getSajdaDetails } from '../utils/sajdaUtils';

export const QuranContext = createContext();

export const QuranProvider = ({ children }) => {
  // Navigation state
  const [currentSurah, setCurrentSurah] = useState(1);
  const [currentVerse, setCurrentVerse] = useState(0);
  const [sajdaData, setSajdaData] = useState({});

  const isCurrentVerseSajda = () => {
    if (currentVerse === 0) return false;
    return isSajdaVerse(sajdaData, currentSurah, currentVerse);
  };

  const getCurrentSajdaDetails = () => {
    return getSajdaDetails(sajdaData, currentSurah, currentVerse);
  };

  // Display preferences
  const [showTranslation, setShowTranslation] = useState(true);
  const [showTransliteration, setShowTransliteration] = useState(true);
  const [showArabic, setShowArabic] = useState(true);
  const [selectedReciter, setSelectedReciter] = useState("ar.alafasy");
  const [selectedTranslation, setSelectedTranslation] = useState("en.asad");
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // Loading and error states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Data states
  const [verseData, setVerseData] = useState(null);
  const [audioUrl, setAudioUrl] = useState("");
  const [availableEditions, setAvailableEditions] = useState({ reciters: [], translations: [] });
  const [loadingEditions, setLoadingEditions] = useState(true);
  
  // Audio control states
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isAutoPlaying, setIsAutoPlaying] = useState(false);
  const autoPlayRef = useRef(false);
  
  // Keep ref in sync
  useEffect(() => { autoPlayRef.current = isAutoPlaying; }, [isAutoPlaying]);

  // Refs so the 'ended' handler always advances from fresh values.
  // Declared before startAudio, which closes over advanceVerseAction.
  const currentSurahRef = useRef(currentSurah);
  const currentVerseRef = useRef(currentVerse);
  useEffect(() => { currentSurahRef.current = currentSurah; }, [currentSurah]);
  useEffect(() => { currentVerseRef.current = currentVerse; }, [currentVerse]);

  // Advance to the next verse without stopping autoplay. Reads only refs and
  // state setters, so its identity never changes.
  const advanceVerseAction = useCallback(() => {
    const next = nextPosition(currentSurahRef.current, currentVerseRef.current);
    if (!next) {
      setIsAutoPlaying(false);
      return;
    }
    setCurrentSurah(next.surah);
    setCurrentVerse(next.verse);
  }, []);

  // ---- Helper: create audio, attach ended handler, play it ----
  const startAudio = useCallback((url) => {
    return new Promise((resolve, reject) => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current = null;
      }

      const audio = new Audio(url);

      audio.addEventListener('ended', () => {
        setIsPlaying(false);
        if (autoPlayRef.current) {
          advanceVerseAction();
        }
      });

      audioRef.current = audio;
      audio.play().then(() => {
        setIsPlaying(true);
        resolve();
      }).catch(err => {
        console.error("Error playing audio:", err);
        setIsPlaying(false);
        if (autoPlayRef.current) {
          setIsAutoPlaying(false);
        }
        reject(err);
      });
    });
  }, [advanceVerseAction]);

  // ---- Stop audio ----
  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
      setIsPlaying(false);
    }
  };

  // ---- Load sajda data ----
  useEffect(() => {
    const loadSajdaData = async () => {
      try {
        const response = await fetch('/paste.txt');
        if (response.ok) {
          const rawData = await response.text();
          setSajdaData(parseSajdaData(rawData));
        }
      } catch (error) {
        console.error('Error loading sajda data:', error);
      }
    };
    loadSajdaData();
  }, []);

  // ---- Load editions ----
  useEffect(() => {
    const getEditions = async () => {
      setLoadingEditions(true);
      try {
        const editions = await fetchEditions();
        setAvailableEditions(editions);
      } catch (error) {
        console.error("Error fetching editions:", error);
      } finally {
        setLoadingEditions(false);
      }
    };
    getEditions();
  }, []);

  // ---- Load saved progress ----
  useEffect(() => {
    // A link someone shared names an exact verse: it wins over where this
    // browser had left off. Preferences still come from the saved progress.
    const linked = parseDeepLink(window.location.hash);
    const progress = loadReadingProgress();

    if (progress) {
      const { surah, verse, translation, transliteration, arabic, reciter, translationEdition } = progress;
      const savedSurah = parseInt(surah, 10) || 1;
      const savedVerse = parseInt(verse, 10) || 1;
      const hasActualProgress = (savedSurah > 1) || (savedVerse > 1);
      if (linked) {
        setCurrentSurah(linked.surah);
        setCurrentVerse(linked.verse);
      } else if (hasActualProgress) {
        setCurrentSurah(savedSurah);
        setCurrentVerse(savedVerse);
      } else {
        setCurrentSurah(1);
        setCurrentVerse(1);
      }
      setShowTranslation(translation !== false);
      setShowTransliteration(transliteration !== false);
      setShowArabic(arabic !== false);
      if (reciter) setSelectedReciter(reciter);
      if (translationEdition) setSelectedTranslation(translationEdition);
    } else {
      setCurrentSurah(linked ? linked.surah : 1);
      setCurrentVerse(linked ? linked.verse : 1);
    }
    setInitialLoadComplete(true);
  }, []);

  // ---- Keep the address bar in step with the verse on screen ----
  useEffect(() => {
    if (!initialLoadComplete) return;
    // replaceState rather than assigning location.hash: it neither fires
    // hashchange nor adds an entry per verse to the back stack.
    window.history.replaceState(null, '', formatDeepLink(currentSurah, currentVerse));
  }, [initialLoadComplete, currentSurah, currentVerse]);

  // ---- Follow the fragment when the reader edits it or uses back/forward ----
  useEffect(() => {
    const handleHashChange = () => {
      const linked = parseDeepLink(window.location.hash);
      if (!linked) return;
      setCurrentSurah(linked.surah);
      setCurrentVerse(linked.verse);
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // ---- Save progress ----
  useEffect(() => {
    if (initialLoadComplete) {
      saveReadingProgress(currentSurah, currentVerse, {
        translation: showTranslation,
        transliteration: showTransliteration,
        arabic: showArabic,
        reciter: selectedReciter,
        translationEdition: selectedTranslation
      });
    }
  }, [
    initialLoadComplete, currentSurah, currentVerse,
    showTranslation, showTransliteration, showArabic,
    selectedReciter, selectedTranslation
  ]);

  // ---- Stop audio on verse change (only when NOT autoplaying) ----
  useEffect(() => {
    stopAudio();
  }, [currentSurah, currentVerse]);

  // ---- Fetch verse data + trigger autoplay directly with fresh URL ----
  useEffect(() => {
    let cancelled = false;

    const getVerseData = async () => {
      setLoading(true);
      setError(null);
      // With no connection, go straight to the downloaded text instead of
      // waiting for a request that cannot succeed.
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        const pack = await ensurePack();
        const stored = verseFromPack(pack, currentSurah, currentVerse, selectedTranslation);
        if (stored && !cancelled) {
          setVerseData(stored);
          setAudioUrl('');
          setLoading(false);
          return;
        }
      }

      try {
        const data = await fetchVerseCached(
          fetchVerseData, currentSurah, currentVerse, selectedReciter, selectedTranslation
        );

        if (cancelled) return;

        setVerseData(data);
        setAudioUrl(data.audioUrl);
        setLoading(false);

        // Warm the neighbours so the next tap is instant. Reading forward is the
        // common case, so the next verse goes first.
        const after = nextPosition(currentSurah, currentVerse);
        const before = previousPosition(currentSurah, currentVerse);
        [after, before].forEach((position) => {
          if (position) {
            prefetchVerse(
              fetchVerseData, position.surah, position.verse,
              selectedReciter, selectedTranslation
            );
          }
        });

        // If autoplay is active, play the freshly fetched audio URL directly
        if (autoPlayRef.current && data.audioUrl) {
          // Small delay to let UI render the new verse
          setTimeout(() => {
            if (!cancelled && autoPlayRef.current) {
              startAudio(data.audioUrl);
            }
          }, 300);
        } else if (autoPlayRef.current && currentVerse === 0) {
          // Bismillah: fetch audio from surah 1 verse 1
          setTimeout(async () => {
            if (!cancelled && autoPlayRef.current) {
              try {
                const bismillahData = await fetchVerseCached(fetchVerseData, 1, 1, selectedReciter, selectedTranslation);
                if (!cancelled && autoPlayRef.current && bismillahData.audioUrl) {
                  startAudio(bismillahData.audioUrl);
                } else if (autoPlayRef.current) {
                  // No bismillah audio, skip to verse 1
                  advanceVerseAction();
                }
              } catch (err) {
                console.error("Error fetching bismillah audio for autoplay:", err);
                if (autoPlayRef.current) setIsAutoPlaying(false);
              }
            }
          }, 300);
        }
      } catch (error) {
        if (cancelled) return;

        // The request failed — fall back to the downloaded text if it is there.
        const pack = await ensurePack();
        const stored = verseFromPack(pack, currentSurah, currentVerse, selectedTranslation);
        if (stored && !cancelled) {
          setVerseData(stored);
          setAudioUrl('');
          setLoading(false);
          return;
        }

        console.error("Error fetching verse data:", error);
        setError("Failed to load verse data. Please check your connection and try again.");
        setLoading(false);

        if (currentVerse === 0) {
          setVerseData({
            arabic: "بِسْمِ اللَّهِ الرَّحْمَٰنِ الرَّحِيمِ",
            translation: "In the name of Allah, the Entirely Merciful, the Especially Merciful.",
            transliteration: "Bismillāhi r-raḥmāni r-raḥīm",
            audioUrl: ""
          });
        } else {
          setVerseData({
            arabic: "Error loading verse",
            translation: "Error loading translation",
            transliteration: "Error loading transliteration"
          });
        }

        if (autoPlayRef.current) setIsAutoPlaying(false);
      }
    };

    getVerseData();
    return () => { cancelled = true; };
  }, [currentSurah, currentVerse, selectedReciter, selectedTranslation, startAudio, advanceVerseAction]);

  // ---- Manual play/pause ----
  const playAudio = async () => {
    // If playing, pause
    if (isPlaying && audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
      if (isAutoPlaying) setIsAutoPlaying(false);
      return;
    }

    // For Bismillah, fetch special audio
    if (currentVerse === 0) {
      try {
        setLoading(true);
        const bismillahData = await fetchVerseCached(fetchVerseData, 1, 1, selectedReciter, selectedTranslation);
        setLoading(false);
        if (bismillahData.audioUrl) {
          await startAudio(bismillahData.audioUrl);
        } else {
          setError("Audio not available for Bismillah. Please try another reciter.");
        }
      } catch (err) {
        console.error("Error fetching Bismillah audio:", err);
        setError("Failed to load Bismillah audio.");
        setLoading(false);
      }
      return;
    }

    // Regular verse
    if (audioUrl) {
      await startAudio(audioUrl);
    } else {
      setError("Audio not available for this verse. Please try another reciter.");
    }
  };

  // ---- Toggle autoplay ----
  const toggleAutoPlay = async () => {
    if (isAutoPlaying) {
      setIsAutoPlaying(false);
      stopAudio();
    } else {
      setIsAutoPlaying(true);
      // Start playing current verse immediately if not already playing
      if (!isPlaying) {
        if (currentVerse === 0) {
          try {
            const bismillahData = await fetchVerseCached(fetchVerseData, 1, 1, selectedReciter, selectedTranslation);
            if (bismillahData.audioUrl) {
              await startAudio(bismillahData.audioUrl);
            } else {
              advanceVerseAction();
            }
          } catch (err) {
            console.error("Error starting autoplay on Bismillah:", err);
            setIsAutoPlaying(false);
          }
        } else if (audioUrl) {
          await startAudio(audioUrl);
        }
      }
    }
  };

  // ---- Navigation (public — stops autoplay) ----
  const goToNextVerse = () => {
    if (isAutoPlaying) { setIsAutoPlaying(false); stopAudio(); }
    goToNextVersePublic();
  };

  const goToNextVersePublic = () => {
    const next = nextPosition(currentSurah, currentVerse);
    if (!next) return;
    setCurrentSurah(next.surah);
    setCurrentVerse(next.verse);
  };

  const goToPreviousVerse = () => {
    if (isAutoPlaying) { setIsAutoPlaying(false); stopAudio(); }

    const previous = previousPosition(currentSurah, currentVerse);
    if (!previous) return;
    setCurrentSurah(previous.surah);
    setCurrentVerse(previous.verse);
  };

  const navigateTo = (surah, verse) => {
    if (isAutoPlaying) { setIsAutoPlaying(false); stopAudio(); }
    setCurrentSurah(surah);
    setCurrentVerse(verse === 1 && surahHasBismillah(surah) ? 0 : verse);
  };

  const resetProgress = () => {
    if (window.confirm('Are you sure you want to reset your reading progress?')) {
      localStorage.removeItem(STORAGE_KEYS.PROGRESS);
      setCurrentSurah(1);
      setCurrentVerse(0);
      if (isAutoPlaying) { setIsAutoPlaying(false); stopAudio(); }
    }
  };

  return (
    <QuranContext.Provider 
      value={{
        currentSurah, currentVerse,
        isCurrentVerseSajda, getCurrentSajdaDetails,

        showTranslation, setShowTranslation,
        showTransliteration, setShowTransliteration,
        showArabic, setShowArabic,
        selectedReciter, setSelectedReciter,
        selectedTranslation, setSelectedTranslation,

        loading, error,
        verseData, availableEditions, loadingEditions,

        isPlaying, playAudio,
        isAutoPlaying, toggleAutoPlay,

        goToNextVerse, goToPreviousVerse, navigateTo,
        resetProgress
      }}
    >
      {children}
    </QuranContext.Provider>
  );
};